"use client";

// La mécanique du rail, et rien d'autre.
//
// Les cartes ne sont PAS construites ici : elles arrivent en children, déjà
// rendues par le serveur, pour que leurs liens restent dans le HTML servi.
//
// Deux modes, choisis selon le POINTEUR et non selon la largeur d'écran :
// une fenêtre étroite sur ordinateur garde ses flèches (impossible de
// glisser à la souris), une tablette large garde le glissement au doigt.
//
// - Pointeur précis (souris, trackpad) : le rail se déplace par TRANSLATION,
//   comme chez Netflix. La position est un simple index de première carte,
//   tenu par React : pas de scrollLeft à mesurer, pas de position restaurée
//   par le navigateur, pas d'aimantation qui décale. La flèche « précédent »
//   ne peut apparaître qu'après un vrai clic, par construction.
// - Écran tactile : défilement natif avec aimantation, sans flèches.
//
// Le conteneur s'étend d'un bord de l'écran à l'autre et l'alignement sur la
// colonne du site est un retrait INTÉRIEUR (padding). La découpe se fait donc
// aux bords de l'écran, pas aux bords de la colonne : une fois le rail
// avancé, les cartes précédentes restent visibles dans la gouttière gauche,
// glissées sous le dégradé et la flèche, comme les suivantes dépassent à
// droite.

import { useCallback, useEffect, useRef, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Plafond de la colonne du site (75rem), même valeur que dans --rail-column
// ci-dessous : nécessaire pour compter combien de cartes tiennent dans une
// page. Les gouttières, elles, se lisent sur le padding réel du conteneur.
const COLUMN_MAX_PX = 1200;

export default function SimilarArticlesRail({
    children,
    alignPaddingClass,
    labels,
    count,
}) {
    const viewportRef = useRef(null);
    const trackRef = useRef(null);

    // L'index et le mode vivent en double : dans l'état pour l'affichage,
    // dans une ref pour que les callbacks (redimensionnement, clics) lisent
    // la valeur courante sans se réabonner.
    const indexRef = useRef(0);
    const pagedRef = useRef(false);
    const [index, setIndex] = useState(0);
    const [paged, setPaged] = useState(false);
    const [perPage, setPerPage] = useState(4);

    const measure = useCallback(() => {
        const viewport = viewportRef.current;
        const track = trackRef.current;
        const card = track ? track.querySelector("[data-rail-card]") : null;
        if (!viewport || !track || !card) return null;

        const gap = parseFloat(getComputedStyle(track).columnGap) || 20;
        // getBoundingClientRect plutôt qu'offsetWidth : les cartes ont des
        // largeurs fractionnaires (pourcentages de la colonne) et l'arrondi
        // d'offsetWidth se cumulerait à chaque page.
        const unit = card.getBoundingClientRect().width + gap;
        // La colonne du site, reconstruite depuis le padding réel : le
        // navigateur a déjà résolu le max() du retrait d'alignement, inutile
        // de dupliquer la formule ici.
        const padding =
            parseFloat(getComputedStyle(viewport).paddingLeft) || 0;
        const column = Math.min(
            viewport.clientWidth - 2 * padding,
            COLUMN_MAX_PX,
        );
        const per = Math.max(1, Math.round((column + gap) / unit));
        return { unit, per };
    }, []);

    // Place le rail sur l'index demandé, borné aux positions légitimes. La
    // dernière page est toujours PLEINE : on recule l'index pour montrer
    // exactement les `per` dernières cartes, comme Netflix, plutôt qu'une
    // page à moitié vide.
    const apply = useCallback(
        (wanted) => {
            const m = measure();
            if (!m) return;

            const maxIndex = Math.max(0, count - m.per);
            const next = pagedRef.current
                ? Math.min(Math.max(wanted, 0), maxIndex)
                : 0;

            trackRef.current.style.transform =
                next > 0 ? `translateX(-${next * m.unit}px)` : "";

            indexRef.current = next;
            setIndex(next);
            setPerPage(m.per);
        },
        [count, measure],
    );

    useEffect(() => {
        // Souris ou doigt ? C'est ce qui décide entre flèches et glissement.
        const pointer = window.matchMedia("(hover: hover) and (pointer: fine)");
        const syncMode = () => {
            pagedRef.current = pointer.matches;
            setPaged(pointer.matches);
            // En mode flèches, le défilement natif n'a plus cours : on efface
            // toute position que le navigateur aurait restaurée, pour que le
            // rail ouvre toujours sur sa première carte.
            if (pointer.matches && viewportRef.current)
                viewportRef.current.scrollLeft = 0;
            apply(indexRef.current);
        };
        syncMode();
        pointer.addEventListener("change", syncMode);

        const onResize = () => apply(indexRef.current);
        window.addEventListener("resize", onResize);

        const observer =
            typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(onResize)
                : null;
        if (observer && viewportRef.current)
            observer.observe(viewportRef.current);

        // Le conteneur est overflow:hidden en mode flèches, mais le
        // navigateur le fait quand même défiler pour révéler un lien focalisé
        // au clavier. On convertit ce défilement sauvage en changement de
        // page propre, sinon translation et scrollLeft s'additionneraient
        // n'importe comment.
        const viewport = viewportRef.current;
        const onScroll = () => {
            if (!pagedRef.current) return;
            if (!viewport || viewport.scrollLeft === 0) return;
            const m = measure();
            const leaked = viewport.scrollLeft;
            viewport.scrollLeft = 0;
            if (m) apply(indexRef.current + Math.round(leaked / m.unit));
        };
        if (viewport)
            viewport.addEventListener("scroll", onScroll, { passive: true });

        return () => {
            pointer.removeEventListener("change", syncMode);
            window.removeEventListener("resize", onResize);
            if (observer) observer.disconnect();
            if (viewport) viewport.removeEventListener("scroll", onScroll);
        };
    }, [apply, measure]);

    const page = (direction) => {
        const m = measure();
        if (!m) return;
        apply(indexRef.current + direction * m.per);
    };

    const canScrollLeft = index > 0;
    const canScrollRight = index < count - perPage;

    const arrowClass =
        "absolute top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-11 h-11 rounded-full bg-white dark:bg-bgDark-300 border border-gray-200 dark:border-grayDark-700 shadow-lg text-gray-900 dark:text-white hover:border-primary hover:text-primary transition-colors";

    return (
        <div className="relative">
            <div
                ref={viewportRef}
                // Le retrait d'alignement est un PADDING sur ce conteneur
                // pleine largeur, pas une marge : la découpe d'un overflow se
                // fait au bord du padding-box, donc les cartes translatées
                // restent visibles en le traversant, jusqu'au bord de
                // l'écran.
                //
                // --rail-column est la largeur de la colonne du site vue
                // depuis la piste : elle s'arrête une gouttière avant le bord
                // droit (20px avant md, 2.5rem ensuite), plafonnée à 75rem.
                // Les cartes s'y réfèrent pour se découper en 2, 3 ou 4.
                className={`${
                    paged
                        ? "overflow-hidden"
                        : "overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-pl-5 md:scroll-pl-[max(2.5rem,calc((100%_-_80rem)/2_+_2.5rem))]"
                } ${alignPaddingClass} [--rail-column:calc(100%_-_1.25rem)] md:[--rail-column:min(100%_-_2.5rem,75rem)]`}
            >
                <div
                    ref={trackRef}
                    className="flex gap-5 pb-1 transition-transform duration-500 ease-out"
                >
                    {children}

                    {/* Respiration de fin de course en mode glissement : le
                        padding droit d'un conteneur défilable est ignoré,
                        seul un vrai élément compte dans la largeur défilable.
                        Inerte en mode flèches, où la course est bornée par
                        l'index. */}
                    <div aria-hidden="true" className="shrink-0 w-px" />
                </div>
            </div>

            {/* Voiles et flèches réservés au mode flèches : au doigt, l'état
                ne suit pas le défilement natif, et la carte qui dépasse du
                bord fait office de signal. */}
            {paged && canScrollLeft && (
                <>
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute left-0 top-0 bottom-1 w-24 bg-gradient-to-r from-white dark:from-bgDark-700 to-transparent z-10"
                    />
                    <button
                        type="button"
                        onClick={() => page(-1)}
                        className={`${arrowClass} left-3`}
                        aria-label={labels.previous}
                    >
                        <SolarIcon
                            name="alt-arrow-left-linear"
                            className="w-6 h-6"
                        />
                    </button>
                </>
            )}

            {paged && canScrollRight && (
                <>
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute right-0 top-0 bottom-1 w-24 bg-gradient-to-l from-white dark:from-bgDark-700 to-transparent z-10"
                    />
                    <button
                        type="button"
                        onClick={() => page(1)}
                        className={`${arrowClass} right-3`}
                        aria-label={labels.next}
                    >
                        <SolarIcon
                            name="alt-arrow-right-linear"
                            className="w-6 h-6"
                        />
                    </button>
                </>
            )}
        </div>
    );
}
